import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Plus, Pencil, Trash2, Eye, FileText } from 'lucide-react';
import { useForms } from '../../hooks/form/useForms';
import { useFormActions } from '../../hooks/form/useFormActions';
import { FormPreviewDialog } from '../../components/forms';
import { Form } from '../../types/form';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { ROUTES } from '../../config/constants/routes';
import { NotificationT, SEVERITY } from '../../types/notification';
import EmptyStateIcon from '../../assets/images/emptyState-illus.svg';

const FormsView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { forms, isLoading, error, selectedOrganizationId, refetch } = useForms();
  const { deleteForm } = useFormActions();
  const { role } = useAuth();

  // UI State
  const [previewForm, setPreviewForm] = useState<Form | null>(null);

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    formId: '',
  });

  // Notification State
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  if (!selectedOrganizationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select an organization to view its forms.</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const handleCreate = () => {
    navigate(ROUTES.FORMS_CREATE);
  };

  const handleEdit = (form: Form) => {
    // Use uuid for API operations
    const identifier = form.uuid || form.formId;
    navigate(ROUTES.FORMS_EDIT.replace(':formId', identifier));
  };

  const handleRowClick = (form: Form) => {
    // Navigate to form detail view
    const identifier = form.uuid || form.formId;
    navigate(ROUTES.FORMS_DETAIL.replace(':formId', identifier));
  };

  const handlePreview = (form: Form) => {
    setPreviewForm(form);
  };

  const handleDeleteClick = (form: Form) => {
    // Use uuid for API operations
    const identifier = form.uuid || form.formId;
    setConfirmDialog({
      open: true,
      formId: identifier,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteForm(confirmDialog.formId);
      await refetch();
      setNotification({
        open: true,
        message: 'Form deleted successfully',
        severity: SEVERITY.SUCCESS,
      });
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to delete form',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setConfirmDialog({ open: false, formId: '' });
    }
  };

  const isAdmin = role !== AUTH_CONSTANTS.ROLES.USER;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Dynamic Forms
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage forms that AI can trigger during conversations
          </Typography>
        </Box>

        {isAdmin && forms.length > 0 && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Plus size={20} />}
            onClick={handleCreate}
            className="forms-tour"
          >
            Create Form
          </Button>
        )}
      </Box>

      {/* Informational text about form capabilities */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          A simple way to capture step-by-step information. Supports simple one-way flow only -
          complex fields or branching logic are not supported.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Need additional features?{' '}
          <a
            href="https://github.com/cacxi/cacxi-issues-portal/issues"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            Post a new issue
          </a>{' '}
          to request them.
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : !forms.length ? (
        <Paper elevation={0}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, py: 6 }}
          >
            <img className="w-28 mx-auto" src={EmptyStateIcon} alt="No forms found" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              No forms created yet
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, textAlign: 'center', maxWidth: 400 }}
            >
              Create dynamic forms that can be triggered by AI during customer conversations to
              collect structured data.
            </Typography>
            {isAdmin && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<Plus size={20} />}
                onClick={handleCreate}
                sx={{ mt: 3 }}
                className="forms-tour"
              >
                Create Your First Form
              </Button>
            )}
          </Box>
        </Paper>
      ) : (
        <Paper elevation={0}>
          <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>
                    Form Name
                  </TableCell>
                  <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>
                    Trigger Keywords
                  </TableCell>
                  <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Fields</TableCell>
                  <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Status</TableCell>
                  {isAdmin && (
                    <TableCell
                      align="right"
                      sx={{ py: 1, color: theme.palette.text.secondary, minWidth: 150 }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map(form => (
                  <TableRow
                    key={form.formId}
                    hover
                    onClick={() => handleRowClick(form)}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell sx={{ fontSize: 16, px: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={18} color={theme.palette.primary.main} />
                        <Box>
                          <Typography sx={{ fontWeight: 500 }}>{form.formName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {form.description || 'No description'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 250 }}>
                        {(() => {
                          const keywords = (form.triggerPrompt || '')
                            .split(',')
                            .filter(k => k.trim());
                          if (keywords.length === 0) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                No keywords
                              </Typography>
                            );
                          }
                          return (
                            <>
                              {keywords.slice(0, 3).map((keyword, idx) => (
                                <Chip
                                  key={idx}
                                  label={keyword.trim()}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${theme.palette.primary.main}10`,
                                    color: theme.palette.primary.main,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              ))}
                              {keywords.length > 3 && (
                                <Chip
                                  label={`+${keywords.length - 3} more`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {Array.isArray(form.fields) ? form.fields.length : 0} field
                        {(Array.isArray(form.fields) ? form.fields.length : 0) !== 1 ? 's' : ''}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          form.isActive === true
                            ? 'Active'
                            : form.isActive === false
                              ? 'Inactive'
                              : 'Active'
                        }
                        size="small"
                        color={form.isActive !== false ? 'success' : 'default'}
                      />
                    </TableCell>

                    {isAdmin && (
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Tooltip title="Preview">
                          <IconButton
                            onClick={() => handlePreview(form)}
                            size="small"
                            sx={{ mr: 1 }}
                          >
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleEdit(form)} size="small" sx={{ mr: 1 }}>
                            <Pencil size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDeleteClick(form)}
                            size="small"
                            color="error"
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {previewForm && (
        <FormPreviewDialog
          open={!!previewForm}
          onClose={() => setPreviewForm(null)}
          form={previewForm}
        />
      )}

      <ConfirmationDialog
        open={confirmDialog.open}
        title="Delete Form"
        message="Are you sure you want to delete this form? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, formId: '' })}
      />

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default FormsView;
